"use client";

import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import { GridIcon, MapIcon } from '@/assets/icons';
import BellIcon from '@/assets/icons/bell.svg';
import SettingsIcon from '@/assets/icons/settings.svg';
import ShipIcon from '@/assets/icons/ship.svg';
import { useQueryNotificationAlertCountUnread } from '@/api/queries/notification';

import apiConfig from '@/config/url-api';
import NotificationDesktopMenu from "@/featured/apps/components/notification/NotificationDesktopMenu";
import SettingsDesktopMenu from '@/featured/apps/components/settings/SettingsDesktopMenu';
import useAuthStore from '@/store/auth';
import UserDropdown from '../dashboard/header/UserDropDown';
import { Ratio } from 'lucide-react';
import { useIsMobile } from '@/utils/hooks/use-mobile';

export default function DesktopNav() {
  const sidebar = useAuthStore((state) => state.sidebar);
  const router = useRouter();
  const notificationAlertCountUnread = useQueryNotificationAlertCountUnread();
  const isMobile = useIsMobile();

  const currentBasePath = router.asPath.split('#')[0].split('?')[0] || '/dashboard';

  const isActive = (item: { label: string; href: string }) => {
    if (item.label === 'Notification') {
      return router.asPath.includes('#notification');
    }
    if (item.label === 'Settings') {
      return router.asPath.includes('#settings');
    }
    if (router.asPath.includes('#notification') || router.asPath.includes('#settings')) {
      return false;
    }
    if (item.href === '/dashboard') {
      return router.asPath.startsWith('/dashboard');
    }
    if (item.label === 'Apps') {
      return router.pathname === '/apps';
    }
    return router.asPath === item.href;
  };

  const logout = () => {
    localStorage.removeItem(apiConfig.auth.storageTokenKeyName);
    localStorage.removeItem(apiConfig.auth.storageUserKeyName);
    window.location.href = '/';
  };

  type NavItem = {
    icon: React.ReactNode;
    label: string;
    href: string;
    isHash?: boolean;
    highlight?: boolean;
  };

  const navItems: NavItem[] = [
    {
      icon: <MapIcon className="h-5 w-5" />,
      label: 'Apps',
      href: '/apps',
    },
    {
      icon: <GridIcon className="h-5 w-5" />,
      label: 'Grid Maps',
      href: '/grid-maps',
    },
    {
      icon: <Ratio className="h-5 w-5" />,
      label: 'Dashboard',
      href: '/dashboard',
    },
    {
      icon: <BellIcon className="h-5 w-5" />,
      label: 'Notification',
      href: `${currentBasePath}#notification`,
      isHash: true,
    },
    {
      icon: <SettingsIcon className="h-5 w-5" />,
      label: 'Settings',
      href: `${currentBasePath}#settings`,
      isHash: true,
    },
  ];

  const totalUnread =
    (notificationAlertCountUnread?.data?.notification_zone ?? 0) +
    (notificationAlertCountUnread?.data?.notification_port ?? 0) +
    (notificationAlertCountUnread?.data?.voyager_alarm ?? 0);

  return (
    <>
      <div
        className={`fixed inset-y-0 left-0 z-40 w-[72px] flex flex-col items-center bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transition-transform duration-300 ${
          sidebar ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Logo Container */}
        <div className="h-[72px] flex items-center justify-center w-full border-b border-gray-100 dark:border-gray-800 shrink-0">
          <Link href="/" className="block outline-none" title="Home">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 dark:bg-brand-900/40 shadow-sm border border-brand-100 dark:border-brand-800/30">
              <img src="/images/logo-missi.png" className="w-6 h-6 object-contain" alt="Missi" />
            </div>
          </Link>
        </div>

        {/* Navigation Content */}
        <nav className="flex-1 py-3 flex flex-col items-center gap-1.5 w-full px-1 overflow-y-auto no-scrollbar">
          {navItems.map((item, index) => {
            const active = isActive(item);

            return (
              <Link
                key={index}
                href={item.href}
                shallow={item.isHash}
                className={`group relative w-[62px] py-2 px-1 rounded-xl flex flex-col items-center justify-center transition-all duration-200 shrink-0 ${
                  active
                    ? 'bg-brand-50 dark:bg-brand-900/40 text-brand-500 dark:text-brand-400'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                {/* Active indicator bar */}
                {active && (
                  <span className="absolute -left-[5px] top-2 bottom-2 w-1 rounded-r-full bg-brand-500" />
                )}

                <div className="relative flex items-center justify-center">
                  {item.icon}
                  {item.label === 'Notification' && totalUnread > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                    </span>
                  )}
                </div>

                {/* Menu Name below icon */}
                <span
                  className={`text-[10px] leading-tight mt-1 text-center truncate w-full tracking-tight ${
                    active
                      ? 'font-semibold text-brand-600 dark:text-brand-400'
                      : 'font-medium text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-100'
                  }`}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </nav>

        {/* Profile / Bottom Section */}
        <div className="pb-3 pt-2 mt-auto shrink-0 flex flex-col items-center w-full px-1">
          <div className="w-[62px] py-1.5 flex flex-col items-center justify-center">
            <UserDropdown />
            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mt-1 text-center truncate w-full tracking-tight">
              Account
            </span>
          </div>
        </div>
      </div>

      {!isMobile && (
        <>
          <NotificationDesktopMenu />
          <SettingsDesktopMenu />
        </>
      )}
    </>
  );
}
